<template>
  <div class="settings-page">
    <div class="settings-header" v-if="databases.length > 1">
      <label class="db-label">Database:</label>
      <select class="db-select" v-model="selectedDatabase" @change="onDatabaseChange">
        <option v-for="db in databases" :key="db" :value="db">{{ db }}</option>
      </select>
    </div>

    <TabView :tabs="tabs">
      <template #processes>
        <ViewModeToggle v-model="processViewMode" aria-label="View mode for processes" />
        <div class="create-card">
          <div class="create-card-header">
            <span class="create-card-plus">+</span>
            <span>Add Process</span>
          </div>
          <div class="create-row">
            <div class="field-group">
              <label class="field-label">Process</label>
              <select class="create-select" v-model="newProcess.processName">
                <option value="" disabled>Select a process...</option>
                <option v-for="name in availableProcesses" :key="name" :value="name">{{ name }}</option>
              </select>
            </div>
            <div class="field-group">
              <label class="field-label">Display name</label>
              <input class="create-input" type="text" placeholder="Optional" v-model="newProcess.displayName" />
            </div>
            <div class="field-group">
              <label class="field-label">Hourly cost</label>
              <input class="create-input create-input--number" type="number" min="0" placeholder="0" v-model.number="newProcess.hourlyCost" />
            </div>
            <div class="field-group">
              <label class="field-label">Manual time (min)</label>
              <input class="create-input create-input--number" type="number" min="0" placeholder="0" v-model.number="newProcess.manualTimeMinutes" />
            </div>
            <div class="field-group">
              <label class="field-label">Time savings goal</label>
              <input class="create-input create-input--number" type="number" min="0" placeholder="0" v-model.number="newProcess.timeSavingsGoal" />
            </div>
            <div class="field-group">
              <label class="field-label">Cost savings goal</label>
              <input class="create-input create-input--number" type="number" min="0" placeholder="0" v-model.number="newProcess.costSavingsGoal" />
            </div>
            <div class="field-group field-group--action">
              <label class="field-label">&nbsp;</label>
              <button class="create-btn" :class="{ 'create-btn--invalid': !processFormValid }" @click="handleCreateProcess">
                Create
              </button>
            </div>
          </div>
        </div>
        <FilterTableViewer
          v-show="processViewMode === 'list'"
          ref="processTableRef"
          search-on-column="processName"
          :actions="processActions"
          :ignored-keys="processIgnoredKeys"
          :editable-columns="editableColumns"
          :editable-column-types="editableColumnTypes"
          :row-editable-state="processEditState"
          :toggle-columns="processToggleColumns"
          :badge-columns="processBadgeColumns"
          :disable-column-dropdown="true"
        />
        <GroupsManager
          v-if="processViewMode === 'groups' && selectedDatabase"
          ref="groupsManagerRef"
          :database-display-name="selectedDatabase"
          :service="processGroupService"
          :items="processItems"
          @changed="loadData"
        />
      </template>
      <template #queues>
        <ViewModeToggle v-model="queueViewMode" aria-label="View mode for queues" />
        <div class="create-card">
          <div class="create-card-header">
            <span class="create-card-plus">+</span>
            <span>Add Queue</span>
          </div>
          <div class="create-row">
            <div class="field-group">
              <label class="field-label">Queue</label>
              <select class="create-select" v-model="newQueue.queueName">
                <option value="" disabled>Select a queue...</option>
                <option v-for="name in availableQueues" :key="name" :value="name">{{ name }}</option>
              </select>
            </div>
            <div class="field-group">
              <label class="field-label">Display name</label>
              <input class="create-input" type="text" placeholder="Optional" v-model="newQueue.displayName" />
            </div>
            <div class="field-group">
              <label class="field-label">Hourly cost</label>
              <input class="create-input create-input--number" type="number" min="0" placeholder="0" v-model.number="newQueue.hourlyCost" />
            </div>
            <div class="field-group">
              <label class="field-label">Manual time (min)</label>
              <input class="create-input create-input--number" type="number" min="0" placeholder="0" v-model.number="newQueue.manualTimeMinutes" />
            </div>
            <div class="field-group">
              <label class="field-label">Time savings goal</label>
              <input class="create-input create-input--number" type="number" min="0" placeholder="0" v-model.number="newQueue.timeSavingsGoal" />
            </div>
            <div class="field-group">
              <label class="field-label">Cost savings goal</label>
              <input class="create-input create-input--number" type="number" min="0" placeholder="0" v-model.number="newQueue.costSavingsGoal" />
            </div>
            <div class="field-group field-group--action">
              <label class="field-label">&nbsp;</label>
              <button class="create-btn" :class="{ 'create-btn--invalid': !queueFormValid }" @click="handleCreateQueue">
                Create
              </button>
            </div>
          </div>
        </div>
        <FilterTableViewer
          v-show="queueViewMode === 'list'"
          ref="queueTableRef"
          search-on-column="queueName"
          :actions="queueActions"
          :ignored-keys="queueIgnoredKeys"
          :editable-columns="editableColumns"
          :editable-column-types="editableColumnTypes"
          :row-editable-state="queueEditState"
          :toggle-columns="queueToggleColumns"
          :badge-columns="queueBadgeColumns"
          :disable-column-dropdown="true"
        />
        <GroupsManager
          v-if="queueViewMode === 'groups' && selectedDatabase"
          ref="queueGroupsManagerRef"
          :database-display-name="selectedDatabase"
          :service="queueGroupService"
          :items="queueItems"
          kind="roiQueue"
          @changed="loadData"
        />
      </template>
    </TabView>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoiRefreshStore } from '@/stores/roiRefreshStore';
import { useDatabaseStore } from '@/stores/useDatabaseStore';
import TabView from '@/components/TabView.vue';
import FilterTableViewer from '@/components/Automation/FilterTableViewer.vue';
import ViewModeToggle from '@/components/Groups/ViewModeToggle.vue';
import GroupsManager from '@/components/Groups/GroupsManager.vue';
import {
  getRoiProcesses, getAvailableProcesses, createRoiProcess, updateRoiProcess, deleteRoiProcess,
  getRoiQueues, getAvailableQueues, createRoiQueue, updateRoiQueue, deleteRoiQueue
} from '@/Services/Ressources/API/RoiAdminService';
import * as RoiProcessGroupApi from '@/Services/Ressources/API/RoiProcessGroupService';
import * as RoiQueueGroupApi from '@/Services/Ressources/API/RoiQueueGroupService';
import { iconNames } from '@/Modules/utils/icons';
import { withNotification } from '@/Modules/utils/withNotification';

const roiRefreshStore = useRoiRefreshStore();

const tabs = [
  { id: 'processes', label: 'Processes' },
  { id: 'queues', label: 'Queues' }
];

// The selected database is shared across all analytics pages (see useDatabaseStore),
// so a choice made here persists when navigating to Business/Operations and back.
const databaseStore = useDatabaseStore();
const databases = computed(() => databaseStore.databases);
const selectedDatabase = computed({
  get: () => databaseStore.selected,
  set: (value) => databaseStore.setSelected(value)
});
const processTableRef = ref(null);
const queueTableRef = ref(null);
const groupsManagerRef = ref(null);
const queueGroupsManagerRef = ref(null);
const processEditState = reactive({});
const queueEditState = reactive({});
const availableProcesses = ref([]);
const availableQueues = ref([]);
const processViewMode = ref('list');
const processItems = ref([]);
const queueViewMode = ref('list');
const queueItems = ref([]);
const processGroupService = {
  getGroups: RoiProcessGroupApi.getProcessGroups,
  createGroup: RoiProcessGroupApi.createProcessGroup,
  renameGroup: RoiProcessGroupApi.renameProcessGroup,
  deleteGroup: RoiProcessGroupApi.deleteProcessGroup,
  setMembership: RoiProcessGroupApi.setProcessMembership,
};
const queueGroupService = {
  getGroups: RoiQueueGroupApi.getQueueGroups,
  createGroup: RoiQueueGroupApi.createQueueGroup,
  renameGroup: RoiQueueGroupApi.renameQueueGroup,
  deleteGroup: RoiQueueGroupApi.deleteQueueGroup,
  setMembership: RoiQueueGroupApi.setQueueMembership,
};

const blankProcess = () => ({ processName: '', displayName: '', hourlyCost: 0, manualTimeMinutes: 0, timeSavingsGoal: 0, costSavingsGoal: 0 });
const blankQueue = () => ({ queueName: '', displayName: '', hourlyCost: 0, manualTimeMinutes: 0, timeSavingsGoal: 0, costSavingsGoal: 0 });

const newProcess = reactive(blankProcess());
const newQueue = reactive(blankQueue());

// Every numeric ROI field is required and must be a number >= 0. Display name
// stays optional. Returns the first error message, or null when the form is valid.
const ROI_NUMBER_FIELDS = [
  ['hourlyCost', 'Hourly cost'],
  ['manualTimeMinutes', 'Manual time (min)'],
  ['timeSavingsGoal', 'Time savings goal'],
  ['costSavingsGoal', 'Cost savings goal']
];

const validateRoiForm = (form, nameField, nameLabel) => {
  if (!form[nameField]) return `${nameLabel} must be selected`;
  for (const [key, label] of ROI_NUMBER_FIELDS) {
    const value = form[key];
    if (typeof value !== 'number' || !Number.isFinite(value)) return `${label} must be a number`;
    if (value < 0) return `${label} must be 0 or greater`;
  }
  return null;
};

const processFormValid = computed(() => validateRoiForm(newProcess, 'processName', 'Process') === null);
const queueFormValid = computed(() => validateRoiForm(newQueue, 'queueName', 'Queue') === null);

const ignoredKeys = new Set(['transactions', 'totalTimeSaved', 'totalCostSaved']);
const processIgnoredKeys = new Set([...ignoredKeys, 'groupId', 'groupName']);
const queueIgnoredKeys = new Set([...ignoredKeys, 'groupId', 'groupName']);
const processBadgeColumns = { processName: 'groupName' };
const queueBadgeColumns = { queueName: 'groupName' };

const processToggleColumns = {
  isActive: withNotification(async (row) => {
    row.isActive = !row.isActive;
    try {
      await updateRoiProcess(selectedDatabase.value, { ...row });
      roiRefreshStore.markDirty();
    } catch (e) {
      row.isActive = !row.isActive;
      throw e;
    }
  }, {
    successTitle: 'Status updated',
    successMessage: 'Process status was successfully updated.',
    errorTitle: 'Failed to update status',
    fallbackErrorMessage: 'An unexpected error occurred while updating the status.'
  })
};

const queueToggleColumns = {
  isActive: withNotification(async (row) => {
    row.isActive = !row.isActive;
    try {
      await updateRoiQueue(selectedDatabase.value, { ...row });
      roiRefreshStore.markDirty();
    } catch (e) {
      row.isActive = !row.isActive;
      throw e;
    }
  }, {
    successTitle: 'Status updated',
    successMessage: 'Queue status was successfully updated.',
    errorTitle: 'Failed to update status',
    fallbackErrorMessage: 'An unexpected error occurred while updating the status.'
  })
};

const editableColumns = ['hourlyCost', 'manualTimeMinutes', 'timeSavingsGoal', 'costSavingsGoal'];
const editableColumnTypes = {
  hourlyCost: 'number',
  manualTimeMinutes: 'number',
  timeSavingsGoal: 'number',
  costSavingsGoal: 'number'
};

function createRowActions(editState, keyField, saveFn, deleteFn) {
  return (row) => {
    const isEditing = editState[row[keyField]];

    if (isEditing) {
      return [
        {
          label: 'Cancel',
          icon: iconNames.closeBox,
          variant: 'default',
          tooltip: 'Cancel editing',
          handler: (r) => { editState[r[keyField]] = false; }
        },
        {
          label: 'Save',
          icon: iconNames.save,
          variant: 'success',
          tooltip: 'Save row',
          handler: withNotification(async (r) => {
            await saveFn(r);
            editState[r[keyField]] = false;
            roiRefreshStore.markDirty();
          }, {
            successTitle: 'Row saved',
            successMessage: 'The row was successfully saved.',
            errorTitle: 'Failed to save row',
            fallbackErrorMessage: 'An unexpected error occurred while saving the row.'
          })
        }
      ];
    }

    return [
      {
        label: 'Edit',
        icon: iconNames.edit,
        variant: 'primary',
        tooltip: 'Edit row',
        handler: (r) => { editState[r[keyField]] = true; }
      },
      {
        label: 'Delete',
        icon: iconNames.delete,
        variant: 'danger',
        tooltip: 'Delete row',
        handler: withNotification(async (r) => {
          await deleteFn(r[keyField]);
          await loadData();
          roiRefreshStore.markDirty();
        }, {
          successTitle: 'Row deleted',
          successMessage: 'The entry was successfully deleted.',
          errorTitle: 'Failed to delete',
          fallbackErrorMessage: 'An unexpected error occurred while deleting the entry.'
        })
      }
    ];
  };
}

const processActions = createRowActions(
  processEditState,
  'processName',
  (row) => updateRoiProcess(selectedDatabase.value, { ...row }),
  (processName) => deleteRoiProcess(selectedDatabase.value, processName)
);

const queueActions = createRowActions(
  queueEditState,
  'queueName',
  (row) => updateRoiQueue(selectedDatabase.value, { ...row }),
  (queueName) => deleteRoiQueue(selectedDatabase.value, queueName)
);

const handleCreateProcess = withNotification(async () => {
  const validationError = validateRoiForm(newProcess, 'processName', 'Process');
  if (validationError) throw new Error(validationError);
  if (!availableProcesses.value.includes(newProcess.processName)) {
    throw new Error('Select a process from the current database');
  }
  await createRoiProcess(selectedDatabase.value, {
    processName: newProcess.processName,
    displayName: newProcess.displayName || null,
    hourlyCost: newProcess.hourlyCost,
    manualTimeMinutes: newProcess.manualTimeMinutes,
    timeSavingsGoal: newProcess.timeSavingsGoal,
    costSavingsGoal: newProcess.costSavingsGoal,
    isActive: true
  });
  Object.assign(newProcess, blankProcess());
  await loadData();
}, {
  successTitle: 'Process created',
  successMessage: 'The process entry was successfully created.',
  errorTitle: 'Failed to create process',
  fallbackErrorMessage: 'An unexpected error occurred while creating the process.'
});

const handleCreateQueue = withNotification(async () => {
  const validationError = validateRoiForm(newQueue, 'queueName', 'Queue');
  if (validationError) throw new Error(validationError);
  if (!availableQueues.value.includes(newQueue.queueName)) {
    throw new Error('Select a queue from the current database');
  }
  await createRoiQueue(selectedDatabase.value, {
    queueName: newQueue.queueName,
    displayName: newQueue.displayName || null,
    hourlyCost: newQueue.hourlyCost,
    manualTimeMinutes: newQueue.manualTimeMinutes,
    timeSavingsGoal: newQueue.timeSavingsGoal,
    costSavingsGoal: newQueue.costSavingsGoal,
    isActive: true
  });
  Object.assign(newQueue, blankQueue());
  await loadData();
}, {
  successTitle: 'Queue created',
  successMessage: 'The queue entry was successfully created.',
  errorTitle: 'Failed to create queue',
  fallbackErrorMessage: 'An unexpected error occurred while creating the queue.'
});

const loadData = async () => {
  if (!selectedDatabase.value) return;

  const [processes, queues, procNames, queueNames] = await Promise.all([
    getRoiProcesses(selectedDatabase.value),
    getRoiQueues(selectedDatabase.value),
    getAvailableProcesses(selectedDatabase.value),
    getAvailableQueues(selectedDatabase.value)
  ]);

  availableProcesses.value = procNames;
  availableQueues.value = queueNames;
  processItems.value = processes;
  queueItems.value = queues;

  processTableRef.value?.populateComponent(processes);
  queueTableRef.value?.populateComponent(queues);
};

// Clear all per-database UI state so nothing from the previous database can
// leak into the newly selected one (e.g. a stale create-form selection being
// posted to the wrong database).
const resetDatabaseScopedState = () => {
  Object.assign(newProcess, blankProcess());
  Object.assign(newQueue, blankQueue());
  Object.keys(processEditState).forEach(k => delete processEditState[k]);
  Object.keys(queueEditState).forEach(k => delete queueEditState[k]);
  availableProcesses.value = [];
  availableQueues.value = [];
};

const onDatabaseChange = async () => {
  resetDatabaseScopedState();
  await loadData();
};

onMounted(async () => {
  await databaseStore.init();
  if (selectedDatabase.value) {
    await loadData();
  }
});
</script>

<style scoped>
.settings-page {
  padding: 1rem;
}

.settings-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.db-label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.db-select {
  padding: 0.4rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #374151;
  background: white;
  cursor: pointer;
}

.db-select:focus {
  outline: none;
  border-color: var(--highlights);
  box-shadow: 0 0 0 2px var(--highlights-background);
}

/* Create card */
.create-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1rem 1.25rem 1.25rem;
  margin-bottom: 1rem;
}

.create-card-header {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.875rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e2e8f0;
}

.create-card-plus {
  font-size: 16px;
  font-weight: 600;
  color: #64748b;
  line-height: 1;
}

.create-row {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.field-label {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
}

.field-group--action {
  margin-left: auto;
}

.create-select {
  padding: 0.4rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #374151;
  background: white;
  cursor: pointer;
  min-width: 200px;
}

.create-select:focus {
  outline: none;
  border-color: var(--highlights);
  box-shadow: 0 0 0 2px var(--highlights-background);
}

.create-input {
  padding: 0.4rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #374151;
  background: white;
  width: 160px;
}

.create-input--number {
  width: 120px;
}

.create-input:focus {
  outline: none;
  border-color: var(--highlights);
  box-shadow: 0 0 0 2px var(--highlights-background);
}

.create-btn {
  padding: 0.4rem 1.1rem;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  background: var(--highlights, #3b82f6);
  color: white;
  cursor: pointer;
  white-space: nowrap;
}

.create-btn:disabled,
.create-btn--invalid {
  opacity: 0.4;
  cursor: not-allowed;
}

.create-btn:not(:disabled):not(.create-btn--invalid):hover {
  opacity: 0.85;
}
</style>
